import {fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError} from "@reduxjs/toolkit/query/react";
import { updateIsRefreshTokenExpired, updateIsSessionExpired } from "../common.slice";
import { logError } from "../../util/telemetry/error/log-error.util";
// START-CodeLeap [02-Sep-2026]: [#18188866946]
import { findUnreadableFormDataFiles } from "../../util/file-form-data.util";
// /END-CodeLeap [02-Sep-2026]: [#18188866946]


const rawBaseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
    prepareHeaders: (header) => {
        header.set("Accept", "application/json");
        return header;
    },
});

// START-CodeLeap [02-Sep-2026]: [#18188866946] fail an upload before sending it when one of its
// attachments can no longer be read from disk.
//
// [04-Sep-2026]: [PR-2615 review fixes] extracted so it can be applied WITHOUT the status handling
// below. secureStore and promptsStore need the probe (they carry the corp/org/launchpad/askGPTBlue
// uploads) but must not inherit baseQueryWithHttpStatus's other behaviours, which would change
// every one of their ~200 endpoints. See baseQueryWithAttachmentProbe.
//
// The probe is inert for anything that is not a multipart upload: a non-FormData body returns
// null immediately, so a plain JSON request pays nothing.
//
// Params : args (string | FetchArgs) - the request as RTK Query hands it to a baseQuery
// Returns: FetchBaseQueryError shaped as CUSTOM_ERROR/UNREADABLE_ATTACHMENT, or null to proceed
const probeAttachments = async (args: string | FetchArgs): Promise<FetchBaseQueryError | null> => {
    const requestBody = typeof args === "object" ? args.body : undefined;
    if (!(requestBody instanceof FormData)) {
        return null;
    }
    const unreadable = await findUnreadableFormDataFiles(requestBody);
    if (unreadable.length === 0) {
        return null;
    }
    logError(`Unreadable attachment: ${unreadable.length} file(s)`, {
        category: 'validation',
        context: 'baseQuery - attachment no longer readable before send',
    });
    return {
        status: 'CUSTOM_ERROR',
        error: 'UNREADABLE_ATTACHMENT',
        data: { unreadableFiles: unreadable },
    } as FetchBaseQueryError;
};

/**
 * The shared fetchBaseQuery config plus the pre-send attachment probe, and NOTHING ELSE.
 *
 * This is what secureStore and promptsStore use. Both previously declared their own private
 * fetchBaseQuery with exactly this config; they now share this one so the config has a single
 * home, and they gain the probe their upload endpoints need. Deliberately NOT
 * baseQueryWithHttpStatus: that wrapper also dispatches session state on 401/403 and re-wraps
 * every success payload (objects gain `_meta`, arrays and primitives get nested under `payload`),
 * which would apply to every endpoint in those two stores rather than to the uploads this ticket
 * touches. Keeping the blast radius at "multipart requests only" is the point.
 */
export const baseQueryWithAttachmentProbe: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    const unreadableError = await probeAttachments(args);
    if (unreadableError) {
        return { error: unreadableError };
    }
    return rawBaseQuery(args, api, extraOptions);
};
// /END-CodeLeap [02-Sep-2026]: [#18188866946]

export const baseQueryWithHttpStatus: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    // START-CodeLeap [02-Sep-2026]: [#18188866946] the stores already on this wrapper carry the
    // flight-deck manual-instance upload, so the probe applies here too.
    const unreadableError = await probeAttachments(args);
    if (unreadableError) {
        return { error: unreadableError };
    }
    // /END-CodeLeap [02-Sep-2026]: [#18188866946]

    const result = await rawBaseQuery(args, api, extraOptions);
    
    // Extract telemetry metadata
    const requestUrl = result.meta?.request?.url;
    const correlationId = result.meta?.response?.headers?.get('x_error') 
      || result.meta?.response?.headers?.get('cf-ray') 
      || result.meta?.response?.headers?.get('x-correlation-id') 
      || undefined;

    // Handle HTTP errors centrally
    if (result.error) {
        const status = result.error.status;
        const errorData = result.error.data;
        
        let errorString = 'Unknown Error';
        if ('error' in result.error) {
            errorString = String(result.error.error);
        } else if (errorData && typeof errorData === 'object') {
            const data = errorData as Record<string, unknown>;
            if (typeof data.message === 'string') {
                errorString = data.message;
            } else if (typeof data.error === 'string') {
                errorString = data.error;
            }
        } else if (typeof errorData === 'string') {
            errorString = errorData.substring(0, 250);
        }

        if (status === 401) {
            api.dispatch(updateIsSessionExpired(true));
            logError('Session Expired (401)', { category: 'auth_session', status, url: requestUrl });
        } else if (status === 403) {
            api.dispatch(updateIsRefreshTokenExpired(true));
            logError('Refresh Token Expired (403)', { category: 'auth_session', status, url: requestUrl });
        } else if (status === 'PARSING_ERROR') {
            logError(`API Parsing Error: ${errorString}`, { category: 'api_contract', correlationId, url: requestUrl });
        } else if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
            logError(`Network Error: ${errorString}`, { category: 'api_network', status: undefined, url: requestUrl });
        } else if (typeof status === 'number') {
            // General 4xx/5xx HTTP errors
            logError(`HTTP Error ${status}`, { category: 'api_http', status, correlationId, url: requestUrl });
        } else {
            // Fallback for custom/unknown RTK errors
            logError(`Unknown API Error: ${errorString}`, { category: 'api_http', correlationId, url: requestUrl });
        }
    }

    if (result.data !== undefined) {
        // Handle sneaky "successful" HTTP 200 responses that are actually business logic failures
        // Example: { success: false, statusCode: 404 }
        if (typeof result.data === 'object' && result.data !== null && 'success' in result.data) {
            const dataObj = result.data as Record<string, unknown>;
            if (dataObj.success === false) {
                const msg = typeof dataObj.message === 'string' ? dataObj.message : 'Unknown business failure';
                const code = dataObj.statusCode ?? dataObj.errorCode ?? 'N/A';
                logError(`API Business Error: [${code}] ${msg}`, { category: 'api_business', correlationId, url: requestUrl });
            }
        }


        // Safe check to see if the response is a standard JSON object
        const isObject = typeof result.data === "object" && result.data !== null && !Array.isArray(result.data);

        return {
            data: isObject 
                ? {
                    ...(result.data as object),
                    _meta: {
                        httpStatus: result.meta?.response?.status ?? 200,
                    }
                }
                : {
                    payload: result.data,
                    _meta: {
                        httpStatus: result.meta?.response?.status ?? 200,
                    }
                }
        };
    }

  return result;
};